// Rechenteil des Drucks. REINE Funktionen, kein DOM.
//
// Der Druck ist der JAHRESPLAN ZUM VERTEILEN: einmal im Jahr auf EIN Blatt,
// als kompakte Tabelle – links die Aufgaben (Termine mit Datum), rechts dünne
// Balken ohne Text. Gezeichnet wird mit demselben Raster wie am Bildschirm
// (Zeitleiste.tsx), nur mit anderem Gitter und anderen Maßen. Hier steht
// allein, was dafür zu rechnen ist. Die alte App hatte an dieser Stelle eine
// KOPIE der Spaltenlogik (buildColsPDF); hier gibt es nur gitterBauen.
//
// Entwurf: entwurf/druck-entwurf.html (abgenommen 22.09.2026, A3 quer als Standard).

import { ANSICHT_BREITE, gitterBauen, istUeberfaellig, tagAus, tagText, type Gitter } from "./kalender";
import type { Aufgabe, Gruppe, Status, Zeitplan } from "./planner";
import { PRIO_ZEICHEN } from "./texte";

export type Papier = "a4" | "a3";

/** Alle Formate QUER – eine Zeitleiste ist breit, nicht hoch. */
export const PAPIER: Record<Papier, { name: string; breiteMm: number; hoeheMm: number }> = {
  a4: { name: "A4 quer", breiteMm: 297, hoeheMm: 210 },
  a3: { name: "A3 quer", breiteMm: 420, hoeheMm: 297 },
};

export const DRUCK_RAND_MM = 10;

/** Breite der Aufgabenspalte im Druck – breiter als am Schirm, weil dort auch das Datum der Termine steht. */
export const DRUCK_SPALTE_PX = 250;

/** Höhe einer Gruppenzeile im Druck. */
export const DRUCK_GRUPPE_H = 20;

/** Zeilenhöhe der Aufgaben: darunter unleserlich, darüber verschenkter Platz. */
export const DRUCK_ZEILE_MIN = 9;
export const DRUCK_ZEILE_MAX = 16;

/**
 * Feste Höhen ober- und unterhalb der Tabelle. Sie werden als CSS-Variablen an
 * den Druckblock gegeben – Rechnung und Darstellung haben damit EINE Quelle.
 */
export const DRUCK_FEST_PX = { kopf: 48, tabellenkopf: 30, fuss: 22 } as const;

/**
 * Millimeter → CSS-Pixel. Im Druck rechnet der Browser mit 96 Pixeln je Zoll
 * (1 Zoll = 25,4 mm). Das ist keine Bildschirmauflösung, sondern die feste
 * CSS-Konvention – deshalb landet ein 277-px-Kasten auf 277/96 Zoll Papier.
 */
export function mmZuPx(mm: number): number {
  return (mm / 25.4) * 96;
}

/** Nutzbare Blattbreite in Pixeln (Papierbreite abzüglich beider Ränder), abgerundet. */
export function druckBreitePx(papier: Papier): number {
  return Math.floor(mmZuPx(PAPIER[papier].breiteMm - 2 * DRUCK_RAND_MM));
}

/** Nutzbare Blatthöhe in Pixeln, abgerundet. */
export function druckHoehePx(papier: Papier): number {
  return Math.floor(mmZuPx(PAPIER[papier].hoeheMm - 2 * DRUCK_RAND_MM));
}

/** Platz für die Zeitleiste selbst: Blattbreite abzüglich Aufgabenspalte. */
export function druckFensterPx(papier: Papier): number {
  return druckBreitePx(papier) - DRUCK_SPALTE_PX;
}

/**
 * Der gedruckte Zeitraum umfasst immer GANZE Monate: vom Ersten des Von-Monats
 * bis zum Ersten des Monats nach Bis (ausschließlich). So endet kein Blatt
 * mitten im Monat.
 */
export function druckZeitraum(von: string, bis: string): { min: Date; max: Date } {
  const v = tagAus(von);
  const b = tagAus(bis);
  return {
    min: new Date(v.getFullYear(), v.getMonth(), 1),
    max: new Date(b.getFullYear(), b.getMonth() + 1, 1),
  };
}

/** Frühester Start und spätestes Ende aller Aufgaben, heute eingeschlossen – Vorbelegung des Dialogs. */
export function datenBereich(
  aufgaben: readonly { start: string; ende: string }[],
  heute: Date,
): { von: string; bis: string } {
  let von = tagText(heute);
  let bis = von;
  for (const a of aufgaben) {
    if (a.start < von) von = a.start;
    if (a.ende > bis) bis = a.ende;
  }
  return { von, bis };
}

/**
 * Das Gitter fürs Blatt: Monatsspalten, so breit, dass ALLE zusammen in das
 * Fenster passen – ganzzahlig und ABGERUNDET, damit gitterBauen beim Runden
 * nichts verschiebt und die letzte Spalte nicht über den Rand läuft.
 */
export function druckGitter(min: Date, max: Date, fenster: number): Gitter {
  const anzahl = gitterBauen("monat", 1, min, max).spalten.length;
  const spaltenBreite = Math.max(1, Math.floor(fenster / anzahl));
  return gitterBauen("monat", spaltenBreite / ANSICHT_BREITE.monat, min, max);
}

export type Druckmasse = {
  /** Höhe einer Aufgabenzeile in px. */
  zeile: number;
  /** Schriftgröße in px, aus der Zeilenhöhe abgeleitet. */
  schrift: number;
  /** Falsch, wenn selbst die kleinste lesbare Zeile nicht mehr auf das Blatt passt. */
  passt: boolean;
};

/**
 * Zeilenhöhe so wählen, dass alles auf das Blatt passt: Was nach Kopf,
 * Tabellenkopf, Fuß und Gruppenzeilen übrig bleibt, wird auf die Aufgabenzeilen
 * verteilt – nach unten durch die Lesbarkeit begrenzt, nach oben durch den
 * Sinn (eine Zeile braucht nicht höher zu sein als am Bildschirm).
 */
export function druckMasse(zeilen: number, gruppen: number, papier: Papier): Druckmasse {
  const fest = DRUCK_FEST_PX.kopf + DRUCK_FEST_PX.tabellenkopf + DRUCK_FEST_PX.fuss;
  const frei = druckHoehePx(papier) - fest - gruppen * DRUCK_GRUPPE_H;
  // Ohne Aufgabenzeilen wird frei/0 unendlich – die Obergrenze fängt das ab.
  const roh = Math.floor(frei / zeilen);
  const zeile = Math.min(DRUCK_ZEILE_MAX, Math.max(DRUCK_ZEILE_MIN, roh));
  const schrift = Math.round(Math.min(10.5, Math.max(7, zeile * 0.72)) * 10) / 10;
  return { zeile, schrift, passt: roh >= DRUCK_ZEILE_MIN };
}

export type Druckfilter = {
  /** Gruppen, die in der Filterleiste abgewählt sind. */
  ausgeblendet: ReadonlySet<string>;
  zugeklappt: ReadonlySet<string>;
  /** Status, die in der Filterleiste abgewählt sind. */
  statusAus: ReadonlySet<Status>;
};

/**
 * DIE Filterregel – für Bildschirm UND Blatt. Vorher stand sie zweimal da
 * (hier und in Zeitleiste.tsx); ein neuer Filter an nur einer der beiden
 * Stellen hätte den Druck still anders gefiltert als den Bildschirm.
 */
export function aufgabeSichtbar(a: Aufgabe, filter: Druckfilter): boolean {
  return !filter.ausgeblendet.has(a.gruppeId) && !filter.statusAus.has(a.status);
}

export type Druckauswahl = {
  gruppen: Gruppe[];
  aufgaben: Aufgabe[];
  /** Aufgabenzeilen, die gezeichnet werden (eingeklappte Gruppen zählen keine). */
  zeilen: number;
};

/**
 * Was auf das Blatt kommt: die sichtbaren Gruppen und Aufgaben (Filter wie am
 * Bildschirm), beschränkt auf den Zeitraum, ohne leer gewordene Gruppen.
 * Eine Aufgabe zählt, wenn sie den Zeitraum irgendwo berührt.
 */
export function druckAuswahl(zeitplan: Zeitplan, min: Date, max: Date, filter: Druckfilter): Druckauswahl {
  const minText = tagText(min);
  const maxText = tagText(max); // ausschließlich
  const aufgaben = zeitplan.aufgaben.filter(
    (a) =>
      aufgabeSichtbar(a, filter) &&
      a.ende >= minText &&
      a.start < maxText,
  );
  const belegt = new Set(aufgaben.map((a) => a.gruppeId));
  const gruppen = zeitplan.gruppen.filter((g) => !filter.ausgeblendet.has(g.id) && belegt.has(g.id));
  const zeilen = aufgaben.filter((a) => !filter.zugeklappt.has(a.gruppeId)).length;
  return { gruppen, aufgaben, zeilen };
}

// ── Was auf dem Blatt gezeigt wird ───────────────────────────────────────────

/**
 * Optionen für den Ausdruck. Alles, was am Bildschirm hilft, aber auf einem
 * Blatt, das monatelang hängt, stören oder überholt sein kann.
 */
export type Druckoptionen = {
  /** Linie am Druck-Tag. Auf einem Jahresplan schnell überholt – daher aus. */
  heuteLinie: boolean;
  /** Roter Streifen an Aufgaben, deren Fälligkeit vorbei ist. */
  ueberfaellig: boolean;
  /** Erledigtes blass und durchgestrichen statt wie alles andere. */
  erledigtKennzeichnen: boolean;
  /** Die Zeichen ‼ ! ↓ vor dem Titel. */
  prioritaeten: boolean;
};

export const DRUCK_STANDARD: Druckoptionen = {
  heuteLinie: false,
  ueberfaellig: false,
  erledigtKennzeichnen: true,
  prioritaeten: true,
};

export type Legendeneintrag = "zeitraum" | "einzeltermin" | "erledigt" | "ueberfaellig" | "prioritaet" | "heute";

/**
 * Die Legende des Ausdrucks – abgeleitet, nicht gepflegt.
 *
 * Genannt wird nur, was auf DIESEM Blatt auch vorkommt: eine Form, die keine
 * Aufgabe hat, und eine Kennzeichnung, die abgeschaltet ist, gehören nicht in
 * die Legende. Vorher stand sie fest im Code und behauptete Dinge, die gar
 * nicht gedruckt wurden.
 */
export function druckLegende(
  auswahl: Druckauswahl,
  optionen: Druckoptionen,
  heuteText: string,
): Legendeneintrag[] {
  const a = auswahl.aufgaben;
  const raus: Legendeneintrag[] = [];
  if (a.some((x) => x.start !== x.ende)) raus.push("zeitraum");
  if (a.some((x) => x.start === x.ende)) raus.push("einzeltermin");
  if (optionen.erledigtKennzeichnen && a.some((x) => x.status === "erledigt")) raus.push("erledigt");
  if (optionen.ueberfaellig && a.some((x) => istUeberfaellig(x, heuteText))) raus.push("ueberfaellig");
  // NICHT `!== "mittel"` pruefen: Welche Priorität ein Zeichen trägt, steht in
  // PRIO_ZEICHEN – hier nachgebaut, liefe die Legende auseinander, sobald dort
  // etwas geändert wird. (Gefunden beim Aufräumen am 22.09.2026: von drei
  // Stellen fragten zwei die Tabelle, diese eine baute die Regel nach.)
  if (optionen.prioritaeten && a.some((x) => PRIO_ZEICHEN[x.prioritaet] !== "")) raus.push("prioritaet");
  if (optionen.heuteLinie) raus.push("heute");
  return raus;
}
